import { Col, Form, Input, Row, Select } from 'antd';
import { COLOMBIA_DEPARTMENTS } from '../utils/colombiaDepartments';
import { COLOMBIA_MUNICIPALITIES } from '../utils/colombiaMunicipalities';
import type { LocationFormState } from '../utils/locationForm';

/** Departamentos de Colombia como opciones del `Select` (valor = nombre legible). */
const DEPARTMENT_OPTIONS = COLOMBIA_DEPARTMENTS.map((department) => ({
  value: department,
  label: department,
}));

/**
 * Paso 2 del alta de Locación: dirección y datos de ubicación (state, city, address,
 * postalCode). Reusado por el modal de edición. `country`/`timeZone` no se piden: la
 * plataforma es Colombia-only y hay una sola zona horaria; el backend aplica sus defaults.
 *
 * El municipio (`city`) depende del departamento (`state`): sus opciones salen de
 * `COLOMBIA_MUNICIPALITIES` y hasta que no haya departamento el select queda deshabilitado.
 */
export function LocationAddressFields() {
  const form = Form.useFormInstance<LocationFormState>();
  const state = Form.useWatch('state', form);
  const city = Form.useWatch('city', form);

  const municipalities = state ? COLOMBIA_MUNICIPALITIES[state] ?? [] : [];
  const cityOptions = municipalities.map(({ name }) => ({ value: name, label: name }));
  // Edición de datos existentes: `city` era texto libre y puede no estar en el catálogo.
  // Si es el caso, se agrega como opción para verlo y conservarlo al guardar.
  if (city && !municipalities.some((municipality) => municipality.name === city)) {
    cityOptions.unshift({ value: city, label: city });
  }

  /**
   * Al cambiar el departamento se limpia el municipio si el actual no pertenece al nuevo.
   * Va en el `onChange` del select y no en un efecto sobre `state`: un efecto también
   * dispararía en la carga del modal y borraría un municipio legacy precargado.
   */
  const handleStateChange = () => {
    const nextState = form.getFieldValue('state') as string | undefined;
    const currentCity = form.getFieldValue('city') as string | undefined;
    const allowed = nextState ? COLOMBIA_MUNICIPALITIES[nextState] ?? [] : [];
    if (currentCity && !allowed.some((municipality) => municipality.name === currentCity)) {
      form.setFieldValue('city', undefined);
    }
  };

  return (
    <>
      {/* Departamento primero (el municipio depende de él) y municipio al lado, misma fila. */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Departamento"
            name="state"
            rules={[{ required: true, message: 'Selecciona el departamento.' }]}
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Selecciona el departamento"
              options={DEPARTMENT_OPTIONS}
              onChange={handleStateChange}
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            label="Municipio"
            name="city"
            rules={[{ required: true, message: 'Selecciona el municipio.' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              disabled={!state}
              placeholder={
                state ? 'Selecciona el municipio' : 'Selecciona primero el departamento'
              }
              options={cityOptions}
            />
          </Form.Item>
        </Col>
      </Row>

      {/* Dirección y código postal comparten fila; el código postal es opcional (OCPI).
          Mismo corte 12/12 que la fila de arriba para que los bordes coincidan. */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Dirección"
            name="address"
            rules={[
              {
                required: true,
                whitespace: true,
                message: 'Ingresa la dirección de la ubicación.',
              },
            ]}
          >
            <Input autoComplete="off" placeholder="Introduzca la dirección" />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item label="Código postal" name="postalCode">
            <Input autoComplete="off" placeholder="Introduzca el código postal" />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
}
